import { FiscalDocumentStatus, FiscalProfileStatus } from './fiscalApi';

/**
 * Status tones, kept LOCAL rather than added to `@/components/ui/badge`'s
 * `STATUS_TONES`.
 *
 * Several of fiscal's own values collide by NAME with an existing member and
 * mean something different here: the shared table's `ACCEPTED` is `info`
 * (something is underway, more steps follow) because it means an approval was
 * granted and work continues; a fiscal document's `ACCEPTED` is the authority's
 * final word — the closest thing this domain has to "done, and done well",
 * which is `success` everywhere else in the app. `Pages/Integrations/lib.ts`
 * sets the precedent for keeping a feature's own status vocabulary local for
 * exactly this reason.
 */
export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'brand';

const DOCUMENT_STATUS_TONES: Record<FiscalDocumentStatus, Tone> = {
  DRAFT: 'neutral',
  QUEUED: 'info',
  SUBMITTING: 'info',
  // Waiting on the authority to rule is exactly the "someone else has to
  // answer" state — the same reasoning that makes AWAITING_PICKUP warning-toned
  // on the kitchen board.
  AWAITING_AUTHORITY: 'warning',
  ACCEPTED: 'success',
  // The operator's queue — the loudest tone on the screen, deliberately.
  REJECTED: 'danger',
  CANCELLED: 'neutral',
  ABANDONED: 'danger',
};

export function documentStatusTone(status: FiscalDocumentStatus | string): Tone {
  return DOCUMENT_STATUS_TONES[status as FiscalDocumentStatus] ?? 'neutral';
}

const PROFILE_STATUS_TONES: Record<FiscalProfileStatus, Tone> = {
  DRAFT: 'neutral',
  ACTIVE: 'success',
  DISABLED: 'warning',
};

export function profileStatusTone(status: FiscalProfileStatus | string): Tone {
  return PROFILE_STATUS_TONES[status as FiscalProfileStatus] ?? 'neutral';
}

/** mm:ss-free, locale-aware age of an ISO instant — "the oldest is from Tuesday" made generic. */
export function formatAge(iso: string, locale: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const ms = Date.now() - then;
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return locale.startsWith('ar') ? 'الآن' : 'just now';
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const rtf = new Intl.RelativeTimeFormat(locale.startsWith('ar') ? 'ar' : 'en', { numeric: 'auto' });
  if (days >= 1) return rtf.format(-days, 'day');
  if (hours >= 1) return rtf.format(-hours, 'hour');
  return rtf.format(-minutes, 'minute');
}

export function formatDateTime(value: string | null | undefined, locale: string): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale.startsWith('ar') ? 'ar-SA' : 'en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
